// Billing period selector — prices come from /billing/pricing (admin discounts applied server-side)
import { useEffect, useState } from 'react';
import { ArrowLeft, Check, Loader2, Tag, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api, { apiHelpers } from '@/services/api';
import { FEDERAL_TAX } from '@/constants/provinces';

const FEATURES = {
  starter: ['1-3 properties', 'Unlimited reports', 'Unlimited lookups', 'Unlimited tenants'],
  professional: ['4-10 properties', 'Everything in Starter', 'Priority Support', 'Team Accounts', 'Advanced Analytics'],
  enterprise: ['Unlimited properties', 'Everything in Professional', 'API Access', 'Bulk Operations'],
};

const COLUMNS = [
  { key: 'monthly', title: 'Monthly', billed: () => 'Billed monthly', accent: 'slate' },
  { key: '6_month', title: '6-Month', billed: (p) => `Billed $${p.total.toFixed(2)} every 6 months`, accent: 'amber' },
  { key: 'annual', title: 'Annual', billed: (p) => `Billed $${p.total.toFixed(2)} annually`, accent: 'emerald' },
];

const ACCENT = {
  slate: { border: 'border-slate-200', head: 'bg-slate-50 border-slate-200', badge: 'bg-slate-700', btn: 'bg-teal-600 hover:bg-teal-700' },
  amber: { border: 'border-2 border-amber-500', head: 'bg-amber-50 border-amber-200', badge: 'bg-amber-500', btn: 'bg-amber-600 hover:bg-amber-700' },
  emerald: { border: 'border-2 border-emerald-500', head: 'bg-emerald-50 border-emerald-200', badge: 'bg-emerald-500', btn: 'bg-emerald-600 hover:bg-emerald-700' },
};

export default function BillingIntervalSelector({ plan, province, onBack, onContinue, loading }) {
  const tax = FEDERAL_TAX[province];
  const [basePricing, setBasePricing] = useState(null);
  const [error, setError] = useState(null);
  const [code, setCode] = useState('');
  const [promo, setPromo] = useState(null);
  const [promoError, setPromoError] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    api.get('/billing/pricing').then((r) => setBasePricing(r.data.plans[plan])).catch(() => setError('Could not load pricing'));
  }, [plan]);

  const applyPromo = async () => {
    if (!code.trim()) return;
    setChecking(true);
    setPromoError(null);
    try {
      const r = await api.post('/promo-codes/validate', { code: code.trim(), plan, billing_interval: 'monthly' });
      setPromo({ code: r.data.pricing.promo.code, label: r.data.pricing.promo.duration_label, intervals: r.data.intervals });
    } catch (err) {
      setPromo(null);
      setPromoError(apiHelpers.handleError(err));
    } finally {
      setChecking(false);
    }
  };

  const clearPromo = () => { setPromo(null); setCode(''); setPromoError(null); };
  const pricing = basePricing && promo
    ? Object.fromEntries(Object.keys(basePricing).map((iv) => [iv, promo.intervals[iv] || basePricing[iv]]))
    : basePricing;

  if (!plan) return null;
  const planName = plan.charAt(0).toUpperCase() + plan.slice(1);

  return (
    <div className="space-y-5" data-testid="billing-interval-selector">
      <div>
        <button onClick={onBack} disabled={loading} className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1.5 mb-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <h1 className="text-lg font-semibold text-slate-800">{planName} Plan</h1>
        <p className="text-sm text-slate-500">Choose your billing period{tax ? <span data-testid="interval-tax-note"> · prices before {tax.rate}% {tax.label} ({province}), added at checkout</span> : ' · applicable sales tax added at checkout'}</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-3 max-w-md" data-testid="promo-code-box">
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1.5 inline-flex items-center gap-1"><Tag className="w-3 h-3" />Promo code</p>
        {promo ? (
          <div className="flex items-center justify-between" data-testid="promo-applied">
            <p className="text-sm text-slate-800"><span className="font-mono font-semibold">{promo.code}</span> applied <span className="text-xs text-slate-500">· {promo.label}</span></p>
            <button onClick={clearPromo} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="promo-remove-btn"><X className="w-3 h-3" />Remove</button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setPromoError(null); }} onKeyDown={(e) => e.key === 'Enter' && applyPromo()} placeholder="Enter code" className="h-8 text-sm font-mono" data-testid="promo-code-input" />
            <Button onClick={applyPromo} disabled={checking || !code.trim()} variant="outline" className="h-8 text-sm border-slate-300" data-testid="promo-apply-btn">
              {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
            </Button>
          </div>
        )}
        {promoError && <p className="text-xs text-red-600 mt-1.5" data-testid="promo-error">{promoError}</p>}
      </div>

      {error && <p className="text-sm text-red-600" data-testid="pricing-error">{error}</p>}
      {!pricing && !error && <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}

      {pricing && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {COLUMNS.map((col) => {
            const p = pricing[col.key];
            const a = ACCENT[col.accent];
            return (
              <div key={col.key} className={`bg-white ${a.border} rounded-lg overflow-hidden relative`} data-testid={`interval-card-${col.key}`}>
                {p.discount_percent > 0 && (
                  <div className={`absolute top-0 right-0 px-2 py-1 ${a.badge} text-white text-xs font-semibold rounded-bl`} data-testid={`interval-discount-${col.key}`}>
                    Save {p.discount_percent}%
                  </div>
                )}
                <div className={`px-4 py-3 ${a.head} border-b`}>
                  <h3 className="text-base font-semibold text-slate-800 text-center">{col.title}</h3>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Price</p>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-slate-900" data-testid={`interval-per-month-${col.key}`}>${p.per_month.toFixed(2)}</span>
                      <span className="text-sm text-slate-500">/month</span>
                      {p.discount_percent > 0 && <span className="text-sm text-slate-400 line-through ml-1">${p.base_monthly.toFixed(2)}</span>}
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{col.billed(p)}</p>
                    {p.savings > 0 && <p className="text-xs text-emerald-600 font-medium mt-1">Save ${p.savings.toFixed(2)}</p>}
                    {p.promo && (
                      <p className="text-[11px] text-teal-700 mt-1" data-testid={`interval-promo-${col.key}`}>
                        Promo {p.promo.code}: −${p.promo_savings.toFixed(2)}{p.promo.duration === 'once' ? ` first payment, then $${p.renewal_total.toFixed(2)}` : ''}
                      </p>
                    )}
                    {promo && !p.promo && <p className="text-[11px] text-slate-400 mt-1">Promo not valid for this period</p>}
                    {p.discount_parts.length > 1 && (
                      <p className="text-[11px] text-slate-500 mt-1">{p.discount_parts.map((d) => `${d.label} ${d.percent}%`).join(' + ')}</p>
                    )}
                  </div>

                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Includes</p>
                    <ul className="space-y-1">
                      {FEATURES[plan].map((f, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-slate-700">
                          <Check className="w-3 h-3 text-teal-600 mt-0.5 flex-shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button onClick={() => onContinue(col.key, p.promo ? promo.code : null)} disabled={loading} className={`w-full h-10 ${a.btn} text-white text-sm`} data-testid={`interval-continue-${col.key}`}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
